/**
 * FormBuilderProperties - Right panel of the form builder.
 *
 * Shows editable properties for the selected control, section, or the form itself.
 */

import { cn } from '@/shared/lib/cn';
import { Button, Checkbox, Input, Label, Textarea } from '@/shared/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  CONTROL_SPAN_OPTIONS,
  formatControlType,
  getDefaultNextQuestionId,
  updateOptions,
} from './builder-utils';
import type {
  FormControl,
  FormDefinition,
  FormSection,
  FormTypeOption,
  PropertiesTarget,
} from './types';

interface FormBuilderPropertiesProps {
  form: FormDefinition;
  target: PropertiesTarget;
  formTypeOptions: FormTypeOption[];
  selectedSection: FormSection | null;
  selectedControl: FormControl | null;
  onChangeTarget: (target: PropertiesTarget) => void;
  onUpdateForm: (updates: Partial<FormDefinition>) => void;
  onUpdateSection: (updates: Partial<FormSection>) => void;
  onUpdateControl: (updates: Partial<FormControl>) => void;
  onReplaceControl: (next: FormControl) => void;
}

type ActiveTab = 'settings' | 'validation' | 'appearance';

export default function FormBuilderProperties({
  form,
  target,
  formTypeOptions,
  selectedSection,
  selectedControl,
  onChangeTarget,
  onUpdateForm,
  onUpdateSection,
  onUpdateControl,
  onReplaceControl,
}: FormBuilderPropertiesProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('settings');

  const isOptionControl = selectedControl
    ? ['radio', 'checkbox', 'option', 'switch'].includes(selectedControl.control_type)
    : false;

  const controlOptions = selectedControl?.options || [];

  // Sentinel for "follow the natural order" since Radix Select disallows an
  // empty-string item value. Selecting it clears next_q_id (null), letting
  // syncFormControlLinks fall back to the next control in sequence.
  const NEXT_DEFAULT = '__default__';

  // All other questions in the form, as jump targets for the selected control.
  const nextQuestionTargets = selectedControl
    ? (form.sections ?? []).flatMap((section, sectionIndex) =>
        (section.controls ?? [])
          .filter((c) => c.q_id && c.q_id !== selectedControl.q_id)
          .map((c) => ({
            q_id: String(c.q_id),
            label: `${c.control_name?.trim() || formatControlType(c.control_type)} (#${c.q_id})`,
            sectionName: section.section_name?.trim() || `Section ${sectionIndex + 1}`,
          })),
      )
    : [];

  // Branching is not applicable to conditional_field (its next is computed from
  // the matched branch), so the selector is hidden for that type.
  const supportsNextQuestion =
    !!selectedControl && selectedControl.control_type !== 'conditional_field';

  // syncFormControlLinks resolves next_q_id to a concrete id even for
  // "default" flow, so a control whose next_q_id matches its natural next
  // control is still treated as Default in the selector.
  const defaultNextQId =
    selectedControl && target.type === 'control'
      ? getDefaultNextQuestionId(form.sections ?? [], target.sectionIndex, target.controlIndex)
      : null;
  const nextQuestionValue =
    selectedControl?.next_q_id && String(selectedControl.next_q_id) !== String(defaultNextQId ?? '')
      ? String(selectedControl.next_q_id)
      : NEXT_DEFAULT;

  const tabs: { id: ActiveTab; label: string }[] = [
    { id: 'settings', label: 'Settings' },
    { id: 'validation', label: 'Validation' },
    { id: 'appearance', label: 'Appearance' },
  ];

  return (
    <div className="border rounded-lg bg-white h-full flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-semibold text-zinc-900">Properties</h3>
        <p className="text-xs text-zinc-500">
          {target.type === 'form'
            ? 'Edit form settings'
            : target.type === 'section'
              ? 'Edit section settings'
              : 'Edit field settings'}
        </p>
      </div>

      {/* Target selector */}
      <div className="flex gap-1 px-4 pb-2">
        <button
          type="button"
          onClick={() => onChangeTarget({ type: 'form' })}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-colors',
            target.type === 'form'
              ? 'bg-[#E84A2B] text-white'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
          )}
        >
          Form
        </button>
        <button
          type="button"
          onClick={() => {
            if (target.type === 'section' || target.type === 'control') {
              onChangeTarget({ type: 'section', sectionIndex: target.sectionIndex });
            }
          }}
          disabled={target.type === 'form'}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-colors',
            target.type === 'section'
              ? 'bg-[#E84A2B] text-white'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
            target.type === 'form' && 'opacity-50 cursor-not-allowed',
          )}
        >
          Section
        </button>
        <button
          type="button"
          onClick={() => {
            if (target.type === 'control') {
              onChangeTarget(target);
            }
          }}
          disabled={target.type !== 'control'}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-md transition-colors',
            target.type === 'control'
              ? 'bg-[#E84A2B] text-white'
              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
            target.type !== 'control' && 'opacity-50 cursor-not-allowed',
          )}
        >
          Field
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b mx-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px',
              activeTab === tab.id
                ? 'border-[#E84A2B] text-[#E84A2B]'
                : 'border-transparent text-zinc-500 hover:text-zinc-700',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* SETTINGS TAB */}
        {activeTab === 'settings' && target.type === 'form' && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Form name</Label>
              <Input
                value={form.name}
                onChange={(e) => onUpdateForm({ name: e.target.value })}
                placeholder="Enter form name"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => onUpdateForm({ description: e.target.value })}
                placeholder="Enter form description"
                rows={3}
                className="text-sm"
              />
            </div>
            {formTypeOptions.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Form type</Label>
                <Select
                  value={form.form_type_id != null ? String(form.form_type_id) : ''}
                  onValueChange={(v) => onUpdateForm({ form_type_id: Number(v) })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {formTypeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Layout</Label>
              <Select
                value={form.layout || 'Horizontal'}
                onValueChange={(v) => onUpdateForm({ layout: v })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Horizontal">Horizontal</SelectItem>
                  <SelectItem value="Vertical">Vertical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {activeTab === 'settings' && target.type === 'section' && selectedSection && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Section name</Label>
              <Input
                value={selectedSection.section_name}
                onChange={(e) => onUpdateSection({ section_name: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={selectedSection.section_description ?? ''}
                onChange={(e) => onUpdateSection({ section_description: e.target.value })}
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="section-repeatable"
                checked={Boolean(selectedSection.is_repeatable)}
                onCheckedChange={(checked) =>
                  onUpdateSection({
                    is_repeatable: Boolean(checked),
                    max_repeat: checked
                      ? (selectedSection.max_repeat ?? 100)
                      : selectedSection.max_repeat,
                  })
                }
              />
              <Label htmlFor="section-repeatable" className="text-xs">
                Repeatable section
              </Label>
            </div>
            {selectedSection.is_repeatable && (
              <div className="space-y-1.5">
                <Label className="text-xs">Max repeats</Label>
                <Input
                  type="number"
                  value={selectedSection.max_repeat ?? 100}
                  onChange={(e) => {
                    const parsed = Number(e.target.value);
                    const value = Number.isFinite(parsed)
                      ? Math.min(100, Math.max(1, parsed))
                      : 100;
                    onUpdateSection({ max_repeat: value });
                  }}
                  min={1}
                  max={100}
                  className="h-8 text-sm"
                />
              </div>
            )}
          </>
        )}

        {activeTab === 'settings' && target.type === 'control' && selectedControl && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Field type</Label>
              <Input
                value={formatControlType(selectedControl.control_type)}
                disabled
                className="h-8 text-sm bg-zinc-50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Label</Label>
              <Input
                value={selectedControl.control_name}
                onChange={(e) => onUpdateControl({ control_name: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={selectedControl.control_description}
                onChange={(e) => onUpdateControl({ control_description: e.target.value })}
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Help text</Label>
              <Input
                value={selectedControl.help_text ?? ''}
                onChange={(e) => onUpdateControl({ help_text: e.target.value })}
                className="h-8 text-sm"
                placeholder="Help text shown to user"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="control-required"
                checked={selectedControl.required}
                onCheckedChange={(checked) => onUpdateControl({ required: Boolean(checked) })}
              />
              <Label htmlFor="control-required" className="text-xs">
                Required
              </Label>
            </div>

            {/* Min/Max */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min</Label>
                <Input
                  value={selectedControl.min}
                  onChange={(e) => onUpdateControl({ min: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max</Label>
                <Input
                  value={selectedControl.max}
                  onChange={(e) => onUpdateControl({ max: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Next question (branching) */}
            {supportsNextQuestion && (
              <div className="space-y-1.5">
                <Label className="text-xs">Next question</Label>
                <Select
                  value={nextQuestionValue}
                  onValueChange={(v) =>
                    onUpdateControl({ next_q_id: v === NEXT_DEFAULT ? null : v })
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Default (next in order)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NEXT_DEFAULT}>Default (next in order)</SelectItem>
                    {nextQuestionTargets.map((t) => (
                      <SelectItem key={t.q_id} value={t.q_id}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Where the form goes after this question. Default follows the order of questions.
                </p>
              </div>
            )}

            {/* Options editor for choice types */}
            {isOptionControl && (
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Options</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[11px] px-2"
                    onClick={() => {
                      const next = updateOptions(selectedControl, (opts) => [
                        ...opts,
                        { option: '' },
                      ]);
                      onReplaceControl(next);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                {controlOptions.map((option, index) => (
                  <div key={`option-${index}`} className="flex items-center gap-1">
                    <Input
                      value={option.option}
                      placeholder={`Option ${index + 1}`}
                      onChange={(e) => {
                        const next = updateOptions(selectedControl, (opts) =>
                          opts.map((item, idx) =>
                            idx === index ? { ...item, option: e.target.value } : item,
                          ),
                        );
                        onReplaceControl(next);
                      }}
                      className="h-7 text-xs flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = updateOptions(selectedControl, (opts) =>
                          opts.filter((_, idx) => idx !== index),
                        );
                        onReplaceControl(next);
                      }}
                      className="p-1 rounded hover:bg-zinc-100"
                    >
                      <Trash2 className="w-3 h-3 text-zinc-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* VALIDATION TAB */}
        {activeTab === 'validation' && target.type === 'control' && selectedControl && (
          <>
            <div className="flex items-center gap-2">
              <Checkbox
                id="val-required"
                checked={selectedControl.required}
                onCheckedChange={(checked) => onUpdateControl({ required: Boolean(checked) })}
              />
              <Label htmlFor="val-required" className="text-xs">
                Required field
              </Label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min length</Label>
                <Input
                  value={selectedControl.min}
                  onChange={(e) => onUpdateControl({ min: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max length</Label>
                <Input
                  value={selectedControl.max}
                  onChange={(e) => onUpdateControl({ max: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            {(selectedControl.validations || []).map((validation: any, index: number) => (
              <div key={`val-${index}`} className="flex items-center gap-2">
                <Input
                  value={validation.name || ''}
                  onChange={(e) => {
                    const next = (selectedControl.validations || []).map(
                      (item: any, idx: number) =>
                        idx === index ? { ...item, name: e.target.value } : item,
                    );
                    onUpdateControl({ validations: next });
                  }}
                  placeholder="Rule name"
                  className="h-7 text-xs flex-1"
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = (selectedControl.validations || []).filter(
                      (_: any, idx: number) => idx !== index,
                    );
                    onUpdateControl({ validations: next });
                  }}
                  className="p-1 rounded hover:bg-zinc-100"
                >
                  <Trash2 className="w-3 h-3 text-zinc-400" />
                </button>
              </div>
            ))}
          </>
        )}

        {activeTab === 'validation' && target.type !== 'control' && (
          <p className="text-xs text-zinc-400 text-center py-4">
            Select a field to edit validation rules.
          </p>
        )}

        {/* APPEARANCE TAB */}
        {activeTab === 'appearance' && target.type === 'control' && selectedControl && (
          <div className="space-y-1.5">
            <Label className="text-xs">Control width</Label>
            <Select
              value={selectedControl.control_span_length || '12'}
              onValueChange={(v) => onUpdateControl({ control_span_length: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTROL_SPAN_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {activeTab === 'appearance' && target.type !== 'control' && (
          <p className="text-xs text-zinc-400 text-center py-4">
            Select a field to edit appearance settings.
          </p>
        )}
      </div>
    </div>
  );
}
